"use client";

import type { ReactNode } from "react";
import { Star } from "lucide-react";
import { useSets } from "modules/common/hooks/useSets";
import { getGameMetadata } from "modules/common/gameRegistry";
import type { ShortCardSet } from "modules/common/model/models";
import type { GameId } from "modules/common/model/models";
import type {
  SetIconRenderProps,
  SetIconSize,
} from "modules/common/presentation/gameRenderers";
import { renderCodeSetIcon } from "modules/common/presentation/gameRenderers";
import { cn } from "utils/utils";

export interface SetIconProps {
  setCode: string;
  game: GameId;
  size?: SetIconSize;
  /** Use the registry's self-contained dense-table mark when one exists. */
  variant?: "default" | "chip";
  className?: string;
  "aria-hidden"?: boolean;
  /** Optional pre-loaded set data to avoid async lookup. */
  set?: ShortCardSet;
}

export type GameSetIconProps = SetIconRenderProps;

export function SetCodeMark(props: Omit<SetIconRenderProps, "set">) {
  return renderCodeSetIcon(props);
}

interface SetPromoIndicatorProps {
  children: ReactNode;
  isPromo?: boolean;
  className?: string;
  markerClassName?: string;
}

export function SetPromoIndicator({
  children,
  isPromo = false,
  className,
  markerClassName,
}: SetPromoIndicatorProps) {
  if (!isPromo) return children;

  return (
    <span
      className={cn("relative inline-flex min-w-0 align-middle", className)}
    >
      <span className="inline-flex min-w-0">{children}</span>
      <Star
        className={cn(
          "fill-warning stroke-warning pointer-events-none absolute -right-0.5 -top-0.5 z-10 size-2.5 stroke-[2.5] drop-shadow-sm",
          markerClassName
        )}
        aria-hidden="true"
      />
    </span>
  );
}

export function SetIcon(props: SetIconProps) {
  const { getSetByCode } = useSets();
  const set = props.set ?? getSetByCode(props.setCode, props.game);
  const presentation = getGameMetadata(props.game).presentation;
  const { variant = "default", ...renderProps } = props;
  const renderer =
    variant === "chip" && presentation.renderSetIconChip
      ? presentation.renderSetIconChip
      : presentation.renderSetIcon;

  return renderer({ ...renderProps, set });
}
